import { useId, useState, type FormEvent } from 'react';
import { useSubmitLoi } from '@/api/hooks';
import { Button, ErrorText, HelpTip, Label, fileToBase64, oversizeMessage } from '@dataroom/shared';

/**
 * "Submit your signed LOI" (ADR-0029). The LOI is freeform and signed outside
 * the app: the buyer picks the signed document (PDF, Word, …) and uploads it.
 * Submitting records the LOI and moves the buyer to "LOI submitted"; the hook
 * keeps the button disabled until the refreshed LOI has landed, so a second
 * click can never upload twice.
 */
export function LoiForm({ roomId }: { roomId: string }) {
  const submitLoi = useSubmitLoi(roomId);
  const inputId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    const tooBig = oversizeMessage(file);
    setFileError(tooBig);
    if (tooBig) return;
    setReading(true);
    try {
      const contentBase64 = await fileToBase64(file);
      submitLoi.mutate({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
      });
    } catch {
      setFileError('That file could not be read. Please choose it again.');
    } finally {
      setReading(false);
    }
  }

  const busy = reading || submitLoi.isPending;
  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <div className="space-y-1.5">
        <div className="flex items-center gap-1">
          <Label htmlFor={inputId}>Your signed letter of intent</Label>
          <HelpTip id="buyer.loi.upload" />
        </div>
        <input
          id={inputId}
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf"
          disabled={busy}
          onChange={(e) => {
            setFileError(null);
            submitLoi.reset();
            setFile(e.target.files?.[0] ?? null);
          }}
          className="block w-full text-sm file:mr-3 file:rounded-md file:border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
        />
        <p className="text-xs text-muted-foreground">
          Upload the LOI you have signed (PDF or Word, up to 50 MB). The seller will review it and
          respond here.
        </p>
      </div>
      {fileError ? <ErrorText error={fileError} /> : null}
      {submitLoi.error ? <ErrorText error={submitLoi.error} /> : null}
      <Button type="submit" disabled={busy || !file}>
        {busy ? 'Submitting…' : 'Submit signed LOI'}
      </Button>
    </form>
  );
}
